/**
 * ClosingLetterPanel — generates the letter the conditions above are made into (Lloyd, Aug 21 2026).
 *
 * > *"The closing letter is really a structured list of court conditions and deadlines… I'll have it
 * > generate when a case closes, with the review request in the covering email, and produce a draft
 * > for approval before anything is sent."*
 *
 * **This panel deliberately does not edit conditions.** `CaseConditionsPanel`, directly above it on
 * the page, owns that — entering them, marking them complete, correcting a mis-entry. This one reads
 * the same readiness the server reports and turns it into a document. Two editors for one list on a
 * single page is how the two drift.
 *
 * Two things it does show, because they are refusals staff meet here rather than there:
 *
 *  1. **A live surety bond blocks the letter.** Ending the representation while the firm still
 *     stands surety leaves Lloyd personally liable for a client who no longer has his number. The
 *     warning points up the page to the panel that clears it.
 *  2. **Blockers before the button, not after it.** The letter refuses rather than printing a blank
 *     deadline, and the point at which that is cheapest to discover is before pressing anything.
 *
 * It returns the PDF and the covering email text and **sends neither** — the standing rule for
 * anything reaching a client, and doubly so for the letter saying the representation has ended.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Divider,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ClosingLetterApi, {
  ClosingLetterBlockedError,
  ClosingLetterBlockerDto,
  ClosingLetterResultDto,
} from '../../api/ClosingLetterApi';

interface ClosingLetterPanelProps {
  Ticket_guid: string;
}

/** A base64 PDF the browser can open without the page having to host it anywhere. */
function pdfObjectUrl(base64: string): string {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
}

const ClosingLetterPanel: React.FC<ClosingLetterPanelProps> = ({ Ticket_guid }) => {
  const [blockers, setBlockers] = useState<ClosingLetterBlockerDto[]>([]);
  const [liveBondCount, setLiveBondCount] = useState(0);
  const [conditionCount, setConditionCount] = useState(0);

  const [dispositionDate, setDispositionDate] = useState('');
  const [dispositionDescription, setDispositionDescription] = useState('');
  const [representationEndedAt, setRepresentationEndedAt] = useState('');

  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<ClosingLetterResultDto | null>(null);
  const [letterUrl, setLetterUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadReadiness = useCallback(async () => {
    try {
      const dto = await ClosingLetterApi.conditions(Ticket_guid);
      setBlockers(dto.readiness.blockers);
      setLiveBondCount((dto.liveSuretyBonds ?? []).length);
      setConditionCount(dto.conditions.length);
    } catch {
      // Non-fatal: generate re-checks server-side and returns the same blockers, so a failed
      // preview must not stop someone who knows the case is ready.
    }
  }, [Ticket_guid]);

  useEffect(() => {
    loadReadiness();
  }, [loadReadiness]);

  useEffect(() => {
    return () => {
      if (letterUrl) URL.revokeObjectURL(letterUrl);
    };
  }, [letterUrl]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    setResult(null);
    setBlockers([]);
    try {
      const dto = await ClosingLetterApi.generate(Ticket_guid, {
        dispositionDate,
        dispositionDescription,
        representationEndedAt: representationEndedAt || null,
      });
      setResult(dto);
      setLetterUrl(pdfObjectUrl(dto.pdfBase64));
    } catch (err) {
      if (err instanceof ClosingLetterBlockedError) {
        setBlockers(err.blockers.length > 0 ? err.blockers : [{ index: null, reason: err.message }]);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to generate the closing letter');
      }
    } finally {
      setGenerating(false);
      await loadReadiness();
    }
  };

  const canGenerate =
    dispositionDate !== '' && dispositionDescription.trim() !== '' && !generating;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
        Closing letter
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Built from the conditions recorded above. Produces the PDF and the covering email for review
        — neither is sent.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {liveBondCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>The firm is still on a bond for this client</AlertTitle>
          This letter ends the representation, which is one of the grounds for a motion to withdraw as
          surety. Until the bond is dealt with the letter will be refused — use the surety panel above
          to file the motion, or record the release if the court has already discharged it.
        </Alert>
      )}

      {blockers.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>This case is not ready for a closing letter</AlertTitle>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {blockers.map((blocker, index) => (
              <li key={`blocker-${index}`}>
                {blocker.index !== null ? `Condition ${blocker.index}: ` : ''}
                {blocker.reason}
              </li>
            ))}
          </ul>
          {conditionCount === 0 && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Enter what the order requires in the conditions panel above — the letter is made out of
              those rows.
            </Typography>
          )}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          label="Order signed"
          type="date"
          size="small"
          value={dispositionDate}
          onChange={(e) => setDispositionDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ maxWidth: 190 }}
        />
        <TextField
          label="Disposition"
          size="small"
          value={dispositionDescription}
          onChange={(e) => setDispositionDescription(e.target.value)}
          placeholder="a deferred disposition"
          helperText="Reads inline in the letter"
          sx={{ minWidth: 280 }}
        />
        <TextField
          label="Representation ended"
          type="date"
          size="small"
          value={representationEndedAt}
          onChange={(e) => setRepresentationEndedAt(e.target.value)}
          InputLabelProps={{ shrink: true }}
          helperText="Defaults to the day the order was signed"
          sx={{ maxWidth: 220 }}
        />
      </Stack>

      <Button
        variant="contained"
        onClick={handleGenerate}
        disabled={!canGenerate}
        startIcon={generating ? <CircularProgress size={16} color="inherit" /> : undefined}
      >
        {generating ? 'Generating…' : 'Generate closing letter'}
      </Button>

      {result && (
        <Alert severity="success" sx={{ mt: 2 }}>
          <AlertTitle>Letter generated — not sent</AlertTitle>
          {letterUrl && (
            <MuiLink href={letterUrl} target="_blank" rel="noopener noreferrer">
              Open {result.fileName}
            </MuiLink>
          )}
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
            Covering email — subject: {result.email.subject}
          </Typography>
          <Box
            sx={{ mt: 0.5, p: 1, backgroundColor: 'background.paper', borderRadius: 1 }}
            dangerouslySetInnerHTML={{ __html: result.email.html }}
          />
        </Alert>
      )}

      <Divider sx={{ my: 3 }} />
    </Box>
  );
};

export default ClosingLetterPanel;
